import { microsoftAuth } from "./microsoft-auth.service";

export type AuthMode = "api-key" | "microsoft";

export interface TokenProviderConfig {
  getAuthMethod?: () => string;
  getStoredToken?: () => string | null;
  getConnectionToken?: () => string | null; // Alternative for onSend handler
  storeMicrosoftToken?: (token: string, userInfo?: any) => void;
  forceLogout: (message?: string) => void;
}

let inFlightRefreshPromise: Promise<string> | null = null;

/**
 * TokenProvider - single source of truth for auth headers.
 * API key mode: returns stored token.
 * Microsoft mode: returns stored token; use forceRefreshMicrosoftToken on 401.
 */
export function getAuthHeader(config: TokenProviderConfig): string | null {
  const token = config.getStoredToken?.() || config.getConnectionToken?.();
  if (!token) return null;

  const authMethod = config.getAuthMethod?.() || "api-key";
  if (authMethod === "microsoft") {
    return token; // Already stored as "Bearer ..."
  }
  // API key: token may be stored as "Bearer xxx" - return as-is
  return token;
}

/**
 * Force refresh Microsoft token via Office.auth.getAccessToken.
 * Single-flight: concurrent 401s share one refresh.
 */
export async function forceRefreshMicrosoftToken(config: TokenProviderConfig): Promise<string> {
  if (inFlightRefreshPromise) {
    return inFlightRefreshPromise;
  }

  const refreshPromise = (async (): Promise<string> => {
    try {
      if (!microsoftAuth.isSupported()) {
        throw new Error("Microsoft SSO not supported");
      }
      const result = await microsoftAuth.authenticate();
      if (!result.success || !result.token) {
        throw new Error(result.error || "Failed to refresh token");
      }
      config.storeMicrosoftToken?.(result.token, result.userInfo);
      return `Bearer ${result.token}`;
    } finally {
      inFlightRefreshPromise = null;
    }
  })();

  inFlightRefreshPromise = refreshPromise;
  return refreshPromise;
}
